import React, { useState } from 'react';
import { ClaimResponse as Claim } from '@sentara-health/sentara-api-hooks-core';
import { medicalClaimsConstants } from '../../common/utils/constants/Constants';
import { getNormalizedStatus } from '../../common/utils/ClaimUtils';

// IndexedDB configuration
const dbConfig: {
  dbName: string;
  version: number;
  db: IDBDatabase | null;
} = {
  dbName: 'MedicalClaimsApi',
  version: 1,
  db: null,
};

export const useIndexedDBSearch = () => {
  // TypeAhead Search State
  const [filteredData, setFilteredData] = useState<Claim[]>([]);
  const [searchInputValue, setSearchInputValue] = useState<string>('');
  const [searchNoRecords, setSearchNoRecords] = useState<boolean>(false);
  // suggestion list show/hide state value
  const [isListShow, setIsListShow] = useState(true);

  // Initialize IndexedDB
  const initDB = async () => {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(dbConfig.dbName, dbConfig.version);

      request.onupgradeneeded = (event) => {
        const target = event.target as IDBOpenDBRequest;
        const db = target.result;

        // Create records store if it doesn't exist
        if (!db.objectStoreNames.contains('records')) {
          const newRecordStore = db.createObjectStore('records', {
            keyPath: 'id',
            autoIncrement: true,
          });
          // Add index for created field to enable sorting
          newRecordStore.createIndex(
            'normalizedServiceDate',
            'normalizedServiceDate',
            { unique: false }
          );
        }

        // Create search index store if it doesn't exist
        if (!db.objectStoreNames.contains('searchIndex')) {
          const searchStore = db.createObjectStore('searchIndex', {
            keyPath: ['keyword', 'refId'],
          });
          searchStore.createIndex('keyword', 'keyword', { unique: false });
          searchStore.createIndex('refId', 'refId', { unique: false });
        }
      };

      request.onsuccess = (event) => {
        const target = event.target as IDBOpenDBRequest;
        const db = target.result;
        dbConfig.db = db;
        resolve(db);
      };

      request.onerror = (event) => {
        const target = event.target as IDBOpenDBRequest;
        reject(new Error(target.error?.message || 'Failed to open IndexedDB'));
      };
    });
  };

  // Convert text to searchable keywords
  const tokenize = (text: string) => {
    return text
      .toLowerCase()
      .split(/[^a-z0-9-]+/g)
      .filter(Boolean);
  };

  // Get value from nested object path (e.g., "user.profile.name")
  const getNestedValue = (obj: Record<string, any>, path: string) => {
    const keys = path.split('.');
    let current = obj;

    for (const key of keys) {
      if (!current || typeof current !== 'object') return undefined;
      current = Number.isNaN(Number(key))
        ? current[key]
        : current[Number.parseInt(key, 10)];
    }

    return current;
  };

  // Save data to IndexedDB in chunks for better performance
  const bulkInsertLargeJSON = async (
    jsonArray: any[],
    searchFields: string[] = [],
    chunkSize: number = 500
  ) => {
    if (!Array.isArray(jsonArray)) throw new Error('Input must be an array');
    if (!dbConfig.db) throw new Error('IndexedDB is not initialized');

    const insertedIds: number[] = [];

    // Process data in chunks to avoid blocking the UI
    for (let i = 0; i < jsonArray.length; i += chunkSize) {
      const chunk = jsonArray.slice(i, i + chunkSize);

      // Wait for browser to be idle before processing next chunk
      if (typeof requestIdleCallback !== 'undefined') {
        await new Promise((resolve) => requestIdleCallback(resolve));
      }

      // Save chunk to database
      await saveChunkToDatabase(chunk, searchFields, insertedIds);
    }

    return insertedIds;
  };

  const getTotalAmount = (totalArray: any[]): string => {
    if (!Array.isArray(totalArray)) return '';
    const totalPatientResp = totalArray.find(
      (total) => total.category?.coding?.code === 'totalPatientResponsibility'
    );
    return totalPatientResp?.amount?.value || '';
  };

  const indexedDbInit = async (data: Claim[]) => {
    //  IndexedDB initialization configuration
    await initDB();

    // Clear existing records to prevent duplicates on re-render
    await clearIndexedDB();
    if (!Array.isArray(data)) {
      return;
    }

    const validData = data.filter((claim) => claim && typeof claim === 'object' && claim.status);

    // Process data to add normalized status
    const processedData = validData.map((claim) => ({
      ...claim,
      normalizedStatus: getNormalizedStatus(claim.status as string),
    }));

    // Insert new data into IndexedDB
    await bulkInsertLargeJSON(
      processedData,
      [
        'identifier.0.value', // claim ID
        'extension.1.value', // providerName - JAMIE WATKINS
        'extension.4.value', // practiceName - MEDICAL GROUP SENTARA
        'normalizedStatus', // claim status - PAID (normalized)
        'type.coding.display', // serviceType - OFFICE VISITS|INJECTIONS|CONSULTATION|LAB
        'normalizedServiceDate', // claim date - 04/15/1988
        'total.0.amount.value', // amount (totalPatientResponsibility) - 606.80
      ],
      500
    );
  };

  // Helper function to save a chunk of data
  const saveChunkToDatabase = async (
    chunk: any[],
    searchFields: string[],
    insertedIds: number[]
  ) => {
    const transaction = dbConfig.db!.transaction(
      ['records', 'searchIndex'],
      'readwrite'
    );
    const recordStore = transaction.objectStore('records');
    const indexStore = transaction.objectStore('searchIndex');

    // Save each record and create search index
    for (const record of chunk) {
      const request = recordStore.add(record);
      request.onsuccess = (event) => {
        const recordId = (event.target as IDBRequest).result;
        insertedIds.push(recordId);

        // Create search keywords for this record
        const searchText = searchFields
          .map((field) => {
            const value = getNestedValue(record, field);

            if (value === null || value === undefined) {
              return '';
            }
            if (typeof value === 'string' || typeof value === 'number') {
              return String(value);
            }
            return JSON.stringify(value);
          })
          .join(' ');

        const keywords = tokenize(searchText);
        for (const keyword of keywords) {
          indexStore.put({ keyword, refId: recordId });
        }
      };
    }

    // Wait for transaction to complete
    return new Promise((resolve, reject) => {
      transaction.oncomplete = () => resolve(undefined);
      transaction.onerror = () =>
        reject(new Error(transaction.error?.message || 'Transaction failed'));
    });
  };

  // Find records that start with the given keyword
  const searchByPrefix = async (keyword: string) => {
    if (!dbConfig.db) throw new Error('IndexedDB is not initialized');

    const transaction = dbConfig.db.transaction(
      ['records', 'searchIndex'],
      'readonly'
    );
    const indexStore = transaction.objectStore('searchIndex');
    const recordStore = transaction.objectStore('records');

    // Search for keywords that start with the given prefix
    const keywordIndex = indexStore.index('keyword');
    const keywordRange = IDBKeyRange.bound(keyword, keyword + '\uffff');
    const keywordMatches = await new Promise<any[]>((resolve, reject) => {
      const request = keywordIndex.getAll(keywordRange);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () =>
        reject(
          new Error(request.error?.message || 'Failed to get keyword matches')
        );
    });

    if (keywordMatches.length === 0) return [];

    // Get the actual records for these matches
    const records = await Promise.all(
      keywordMatches.map((match) =>
        getRecordFromStore(recordStore, match.refId)
      )
    );

    return records;
  };

  // Search for records matching all words in the query
  const search = async (query: string) => {
    const words = tokenize(query);
    if (words.length === 0) return [];

    // Find records for each word
    const wordResults = await Promise.all(
      words.map((word) => searchByPrefix(word))
    );

    // Find records that contain ALL words (intersection)
    const recordSets = wordResults.map(
      (results) => new Set(results.map((record) => record.id))
    );

    // Get IDs that appear in all word searches
    const commonIds = Array.from(recordSets[0]).filter((id) =>
      recordSets.every((set) => set.has(id))
    );

    return getByIds(commonIds);
  };

  // Helper to get a single record from store
  const getRecordFromStore = (
    store: IDBObjectStore,
    refId: number
  ): Promise<any> => {
    return new Promise((resolve, reject) => {
      const request = store.get(refId);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => {
        console.log('Error fetching record with refId:', refId);
        reject(new Error(request.error?.message || 'Failed to get record'));
      };
    });
  };
  const sortRecordsByCreated = (records: Claim[]): Claim[] => {
    return records.sort((a, b) => {
      const dateA = new Date(a.created || 0).getTime();
      const dateB = new Date(b.created || 0).getTime();
      return dateB - dateA; // Descending order
    });
  };

  // Get multiple records by their IDs
  const getByIds = async (ids: number[]) => {
    if (ids.length === 0) return [];
    if (!dbConfig.db) throw new Error('IndexedDB is not initialized');

    const transaction = dbConfig.db.transaction('records', 'readonly');
    const store = transaction.objectStore('records');

    // Get all records in parallel
    const records = await Promise.all(
      ids.map((id) => getRecordFromStore(store, id))
    );

    // Sort records by created field in descending order (newest first)
    return sortRecordsByCreated(records);
  };

  // Handle search input changes with minimum 3 characters
  const typeAheadSearch = async (searchText: string): Promise<Claim[]> => {
    setSearchNoRecords(false);
    setFilteredData([]);

    // Return early if search text is empty or too short (and not numeric)
    if (searchText.length === 0) return [];

    const isOnlyNumber = /^\d+$/.test(searchText);
    const meetsLengthRequirement =
      searchText.length >= medicalClaimsConstants?.typeAheadSearchLimit;

    if (!isOnlyNumber && !meetsLengthRequirement) return [];

    try {
      const results: Claim[] = await search(searchText);

      if (results.length === 0) {
        setSearchNoRecords(true);
      } else {
        setFilteredData(results);
      }
      return results;
    } catch (error) {
      console.error('Search error:', error);
      return [];
    }
  };

  // Clear IndexedDB records
  const clearIndexedDB = async () => {
    try {
      if (!dbConfig.db) {
        await initDB();
      }

      if (dbConfig.db) {
        const tx = dbConfig.db.transaction(
          ['records', 'searchIndex'],
          'readwrite'
        );
        const recordsStore = tx.objectStore('records');
        const searchIndexStore = tx.objectStore('searchIndex');

        // Clear all records from both object stores
        recordsStore.clear();
        searchIndexStore.clear();

        // Handle transaction errors
        tx.onerror = (event) => {
          console.log('Transaction failed while clearing IndexedDB:', event);
        };

        tx.oncomplete = () => {
          // console.log('IndexedDB cleared successfully');
        };
      }
    } catch (error) {
      console.log('Error clearing IndexedDB:', error);
    }
  };

  const changeSearchInput = async (value: string) => {
    setSearchInputValue(value);
    typeAheadSearch(value);
    setIsListShow(true);
  };

  return {
    clearIndexedDB,
    changeSearchInput,
    filteredData,
    getTotalAmount,
    indexedDbInit,
    isListShow,
    searchInputValue,
    searchNoRecords,
    setSearchNoRecords,
    setSearchInputValue,
    setFilteredData,
    setIsListShow,
    typeAheadSearch,
  };
};
